import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLangStore } from '../../store/langStore';

interface ExperimentStepperProps {
  steps: string[];
  title?: string;
  categoryColor?: string;
}

const DEFAULT_COLOR = '#00C2A8';

const slideVariants = {
  enterFromRight: {
    x: 60,
    opacity: 0,
  },
  enterFromLeft: {
    x: -60,
    opacity: 0,
  },
  center: {
    x: 0,
    opacity: 1,
  },
  exitToLeft: {
    x: -60,
    opacity: 0,
  },
  exitToRight: {
    x: 60,
    opacity: 0,
  },
};

export function ExperimentStepper({
  steps,
  title,
  categoryColor = DEFAULT_COLOR,
}: ExperimentStepperProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [completed, setCompleted] = useState(false);
  const { lang } = useLangStore();
  const en = lang === 'en';
  const resolvedTitle = title ?? (en ? 'How to do it' : 'Slik gjør du det');

  const totalSteps = steps.length;
  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;

  function handleNext() {
    if (isLast) {
      setCompleted(true);
    } else {
      setDirection('forward');
      setCurrentStep((s) => s + 1);
    }
  }

  function handlePrev() {
    setDirection('backward');
    setCurrentStep((s) => s - 1);
  }

  function handleRestart() {
    setCompleted(false);
    setCurrentStep(0);
    setDirection('forward');
  }

  // Lighten the category color for the card background tint
  const colorHex = categoryColor || DEFAULT_COLOR;

  return (
    <div className="w-full max-w-lg mx-auto my-6 font-display">
      <div
        className="rounded-2xl shadow-card overflow-hidden bg-white"
        style={{ border: `1.5px solid ${colorHex}33` }}
      >
        {/* Colored top bar — 3px, matches design system accent lines */}
        <div
          className="h-1 w-full"
          style={{ backgroundColor: colorHex }}
        />

        <div className="p-5 sm:p-6">
          {/* Title row — sand background header area */}
          {resolvedTitle && (
            <div
              className="flex items-center gap-2 -mx-5 sm:-mx-6 -mt-5 sm:-mt-6 px-5 sm:px-6 py-3 mb-5 rounded-t-2xl"
              style={{ backgroundColor: `${colorHex}12` }}
            >
              <div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: colorHex }}
              />
              <p
                className="text-xs font-bold uppercase tracking-widest"
                style={{ color: colorHex }}
              >
                {resolvedTitle}
              </p>
            </div>
          )}

          {!completed ? (
            <>
              {/* Step counter + progress dots */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-ink-muted">
                  {en ? `Step ${currentStep + 1} of ${totalSteps}` : `Steg ${currentStep + 1} av ${totalSteps}`}
                </span>
                <div className="flex gap-1.5">
                  {steps.map((_, i) => (
                    <div
                      key={i}
                      className="h-2 rounded-full transition-all duration-300"
                      style={{
                        width: i === currentStep ? '20px' : '8px',
                        backgroundColor:
                          i <= currentStep ? colorHex : '#e5e7eb',
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Step content with slide animation */}
              <div className="relative overflow-hidden min-h-[120px] flex items-start">
                <AnimatePresence
                  mode="wait"
                  initial={false}
                  custom={direction}
                >
                  <motion.div
                    key={currentStep}
                    custom={direction}
                    variants={slideVariants}
                    initial={
                      direction === 'forward' ? 'enterFromRight' : 'enterFromLeft'
                    }
                    animate="center"
                    exit={
                      direction === 'forward' ? 'exitToLeft' : 'exitToRight'
                    }
                    transition={{
                      duration: 0.28,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                    className="w-full flex gap-4 items-start"
                  >
                    {/* Step number bubble */}
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg select-none"
                      style={{ backgroundColor: colorHex }}
                    >
                      {currentStep + 1}
                    </div>

                    {/* Step text */}
                    <p className="text-ink font-body text-base sm:text-lg leading-relaxed pt-1.5">
                      {steps[currentStep]}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation buttons */}
              <div className="flex justify-between items-center mt-5 pt-4 border-t border-gray-100">
                <button
                  onClick={handlePrev}
                  disabled={isFirst}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-150 disabled:opacity-0 disabled:pointer-events-none text-ink-muted hover:bg-sand active:scale-95"
                >
                  {en ? '← Previous' : '← Forrige'}
                </button>

                <button
                  onClick={handleNext}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all duration-150 hover:opacity-90 active:scale-95 shadow-sm"
                  style={{ backgroundColor: colorHex }}
                >
                  {isLast ? (en ? 'Done! 🎉' : 'Ferdig! 🎉') : (en ? 'Next →' : 'Neste →')}
                </button>
              </div>
            </>
          ) : (
            /* Completed state — summary view */
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              {/* Celebration header */}
              <div className="text-center mb-5">
                <div className="text-4xl mb-1">🎉</div>
                <p className="text-lg font-bold text-ink">{en ? 'Great job!' : 'Supert jobbet!'}</p>
                <p className="text-sm text-ink-muted mt-0.5">
                  {en ? `You completed all ${totalSteps} steps` : `Du fullførte alle ${totalSteps} steg`}
                </p>
              </div>

              {/* Compact step summary */}
              <div className="space-y-2.5 mb-5">
                {steps.map((step, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.06, duration: 0.22 }}
                    className="flex gap-3 items-start"
                  >
                    {/* Check bubble */}
                    <div
                      className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5"
                      style={{ backgroundColor: colorHex }}
                    >
                      ✓
                    </div>
                    <p className="text-ink-muted text-sm leading-snug">{step}</p>
                  </motion.div>
                ))}
              </div>

              {/* Restart button */}
              <div className="pt-4 border-t border-gray-100 flex justify-center">
                <button
                  onClick={handleRestart}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-150 hover:opacity-80 active:scale-95"
                  style={{ color: colorHex, border: `1.5px solid ${colorHex}` }}
                >
                  {en ? '↺ Start over' : '↺ Start på nytt'}
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
